import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  #onEnd = (event) => this.#end(event);
  #onError = (event) => this.#error(event);
  #onProgress = (event) => this.#progress(event);

  connect() {
    this.#bindButtons();
    this.#bindFields();
    this.#bindDropzones();

    document.addEventListener('direct-upload:end', this.#onEnd);
    document.addEventListener('direct-upload:error', this.#onError);
    document.addEventListener('direct-upload:progress', this.#onProgress);
  }

  disconnect() {
    document.removeEventListener('direct-upload:end', this.#onEnd);
    document.removeEventListener('direct-upload:error', this.#onError);
    document.removeEventListener('direct-upload:progress', this.#onProgress);
  }

  #attachFilesToInput(files, field) {
    const dataTransfer = new DataTransfer();

    for (const file of files) dataTransfer.items.add(file);

    field.files = dataTransfer.files;
  }

  #bindButtons() {
    for (const button of this.element.querySelectorAll('[data-upload-button]')) {
      button.addEventListener('click', (event) => {
        event.currentTarget.closest('[data-upload-wrapper]').querySelector('[data-direct-upload-url]').click();
      });
    }
  }

  #bindDropzones() {
    for (const dropzone of this.element.querySelectorAll('[data-dropzone]')) {
      dropzone.addEventListener('dragover', (event) => {
        event.preventDefault();

        dropzone.classList.add('bg-graycom-soft', 'border-bluecom-soft');
      });

      dropzone.addEventListener('dragleave', () => {
        dropzone.classList.remove('bg-graycom-soft', 'border-bluecom-soft');
      });

      dropzone.addEventListener('drop', (event) => {
        event.preventDefault();

        dropzone.classList.remove('bg-graycom-soft', 'border-bluecom-soft');

        const wrapper = dropzone.closest('[data-upload-wrapper]');
        const field = wrapper.querySelector('input[type="file"]');
        const files = event.dataTransfer.files;

        this.#attachFilesToInput(files, field);
        this.#preview(wrapper, files, field);
      });
    }
  }

  #bindFields() {
    for (const field of this.element.querySelectorAll('[data-direct-upload-url]')) {
      const wrapper = field.closest('[data-upload-wrapper]');

      field.addEventListener('change', (event) => this.#preview(wrapper, event.currentTarget.files, field));
    }
  }

  #end(event) {
    console.log('[upload.end] A direct upload has ended.');

    const bar = this.#progressBar(event);

    if (bar) bar.classList.add('hidden');
  }

  #error(event) {
    console.log('[upload.error] An error occurred.', event.detail.error);

    event.preventDefault();

    const message = event.target.closest('[data-upload-wrapper]')?.querySelector('[data-error]');

    if (message) message.textContent = event.detail.error;
  }

  #parameterize(value) {
    return value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9 -]/g, '')
      .replace(/\s/g, '-');
  }

  #preview(wrapper, files, field) {
    for (const file of files) {
      const reader = new FileReader();

      reader.onload = () => this.#showPreview(wrapper, file, field, reader.result);

      reader.readAsDataURL(file);
    }
  }

  #previewTemplate(source, id) {
    return `
      <div class="photos__link photos__preview" data-upload-template>
        <div class="photos__preview__loading" data-preview-item="${id}">0%</div>
        <img alt="" class="photos__image photos__preview__image" src="${source}">
      </div>
    `;
  }

  #progress(event) {
    console.log('[upload.progress] As requests to store files progress.', event.detail.progress);

    const bar = this.#progressBar(event);

    if (!bar) return;

    const percentage = `${Math.round(event.detail.progress)}%`;

    if (bar.dataset.progressbar === 'true') {
      bar.querySelector('[data-bar]').style.width = percentage;
    } else {
      bar.textContent = percentage;
    }
  }

  #progressBar(event) {
    const id = this.#parameterize(event.detail.file.name);

    const bar =
      this.element.querySelector(`[data-preview-item="${id}"]`) ||
      event.target.closest('[data-upload-wrapper]')?.querySelector('[data-preview-item]');

    if (bar) bar.classList.remove('hidden');

    return bar;
  }

  #showPreview(wrapper, file, field, source) {
    if (field.multiple) {
      wrapper
        .querySelector('[data-upload-images]')
        .insertAdjacentHTML('beforeend', this.#previewTemplate(source, this.#parameterize(file.name)));

      return;
    }

    if (!file.name.endsWith('.pdf')) {
      if (wrapper.dataset.uploadAttribute === 'style') {
        wrapper.style = `background-image: url("${source}")`;
      } else {
        const image = wrapper.querySelector('[data-upload-preview-image]');

        if (image) image.src = source;
      }
    }

    const filename = wrapper.querySelector('[data-filename]');

    if (filename) filename.textContent = file.name;
  }
}
